import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "antiscalants",
    description: "",
    dosage_range: "",
    dosage_min: "",
    dosage_max: "",
    applications: "",
    features: "",
    specifications: "",
    product_url: "",
    matching_criteria: ""
  });

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return;
    }
    fetchProducts();
  }, []);

  const getAuthHeader = () => {
    const auth = localStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category: "antiscalants",
      description: "",
      dosage_range: "",
      dosage_min: "",
      dosage_max: "",
      applications: "",
      features: "",
      specifications: "",
      product_url: "",
      matching_criteria: ""
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      description: product.description,
      dosage_range: product.dosage_range,
      dosage_min: product.dosage_min?.toString() || "",
      dosage_max: product.dosage_max?.toString() || "",
      applications: product.applications?.join(", ") || "",
      features: product.features?.join("\n") || "",
      specifications: JSON.stringify(product.specifications || {}, null, 2),
      product_url: product.product_url || "",
      matching_criteria: JSON.stringify(product.matching_criteria || {}, null, 2)
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        dosage_range: formData.dosage_range,
        dosage_min: parseFloat(formData.dosage_min),
        dosage_max: parseFloat(formData.dosage_max),
        applications: formData.applications.split(",").map(s => s.trim()).filter(Boolean),
        features: formData.features.split("\n").map(s => s.trim()).filter(Boolean),
        specifications: JSON.parse(formData.specifications || "{}"),
        product_url: formData.product_url,
        matching_criteria: JSON.parse(formData.matching_criteria || "{}")
      };

      if (editingProduct) {
        await axios.put(
          `${API}/admin/products/${editingProduct.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success("Product updated successfully");
      } else {
        await axios.post(
          `${API}/admin/products`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success("Product created successfully");
      }

      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product");
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: getAuthHeader()
      });
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-products-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Products</h1>
            <p className="text-slate-600">Manage chemical product catalog</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#00BCD4] hover:bg-[#00acc1]"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Product Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border border-slate-300 rounded px-3 py-2"
                    >
                      <option value="antiscalants">Antiscalants</option>
                      <option value="antifoulants">Antifoulants</option>
                      <option value="cleaners">Cleaners</option>
                      <option value="preservatives">Preservatives</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="dosage_range">Dosage Range</Label>
                    <Input
                      id="dosage_range"
                      value={formData.dosage_range}
                      onChange={(e) => setFormData({...formData, dosage_range: e.target.value})}
                      placeholder="e.g., 2-5 ppm"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="dosage_min">Min ppm</Label>
                    <Input
                      id="dosage_min"
                      type="number"
                      step="0.1"
                      value={formData.dosage_min}
                      onChange={(e) => setFormData({...formData, dosage_min: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="dosage_max">Max ppm</Label>
                    <Input
                      id="dosage_max"
                      type="number"
                      step="0.1"
                      value={formData.dosage_max}
                      onChange={(e) => setFormData({...formData, dosage_max: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="applications">Applications (comma-separated)</Label>
                  <Input
                    id="applications"
                    value={formData.applications}
                    onChange={(e) => setFormData({...formData, applications: e.target.value})}
                    placeholder="Brackish Water RO, Industrial Process Water"
                  />
                </div>

                <div>
                  <Label htmlFor="features">Features (one per line)</Label>
                  <Textarea
                    id="features"
                    value={formData.features}
                    onChange={(e) => setFormData({...formData, features: e.target.value})}
                    rows={4}
                    placeholder="Effective at high recovery rates&#10;NSF/ANSI 60 certified"
                  />
                </div>

                <div>
                  <Label htmlFor="specifications">Specifications (JSON)</Label>
                  <Textarea
                    id="specifications"
                    value={formData.specifications}
                    onChange={(e) => setFormData({...formData, specifications: e.target.value})}
                    rows={4}
                    className="font-mono text-sm"
                    placeholder='{"pH": "2.0 - 3.0", "Specific Gravity": "1.10"}'
                  />
                </div>

                <div>
                  <Label htmlFor="matching_criteria">Matching Criteria (JSON)</Label>
                  <Textarea
                    id="matching_criteria"
                    value={formData.matching_criteria}
                    onChange={(e) => setFormData({...formData, matching_criteria: e.target.value})}
                    rows={4}
                    className="font-mono text-sm"
                    placeholder='{"water_source": ["brackish", "well"]}'
                  />
                </div>

                <div>
                  <Label htmlFor="product_url">Product URL</Label>
                  <Input
                    id="product_url"
                    type="url"
                    value={formData.product_url}
                    onChange={(e) => setFormData({...formData, product_url: e.target.value})}
                    placeholder="https://axeonwater.com/products/..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#00BCD4] hover:bg-[#00acc1]">
                    {editingProduct ? "Update" : "Create"} Product
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <p className="text-slate-600 mb-4">No products found. Click "Add Product" or seed the database.</p>
          </div>
        ) : (
          <div className="admin-table">
            <table className="w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Dosage</th>
                  <th>Applications</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.id}`}>
                    <td className="font-medium">{product.name}</td>
                    <td>
                      <span className="capitalize px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                        {product.category}
                      </span>
                    </td>
                    <td className="font-mono">{product.dosage_range}</td>
                    <td className="max-w-xs truncate">
                      {product.applications?.slice(0, 2).join(", ")}
                    </td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
